/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import { Individu } from '@/types';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const validationInformationsNaissance = (
  data: Partial<Individu>,
  parent: boolean = false,
  filiationIconnue: boolean = false,
  dateNaissanceRecense?: { anneeNaissance: number; moisNaissance: number; jourNaissance: number },
) => {
  const errors = {
    dateNaissance: '',
    sexe: '',
    nomFamille: '',
    prenom: '',
    localiteNaissance: '',
    heureNaissance: '',
    minuteNaissance: '',
    adresseNaissance: '',
    identifiantNationalite: '',
  };

  if (parent && filiationIconnue) {
    return {};
  }

  const dateNaissance = dayjs(
    `${data.anneeNaissance}-${data.moisNaissance}-${data.jourNaissance}`,
    'YYYY-M-D',
  );
  const dateRecensement = data.dateRecensement ? dayjs(data.dateRecensement) : dayjs();

  if (_.isEmpty(dateNaissance)) {
    errors.dateNaissance = 'La date de naissance est obligatoire';
  }

  if (
    !dateNaissance.isValid()
    || !dateNaissance.isSameOrBefore(dayjs(), 'day')
    || dateNaissance.isSameOrBefore('1900-01-01', 'day')
  ) {
    errors.dateNaissance = 'La date de naissance est invalide';
  }

  if (!parent && dateNaissance.isValid() && dateRecensement.isValid()) {
    const ageEnAnnees = dateRecensement.diff(dateNaissance, 'year');
    if (ageEnAnnees < 16) {
      errors.dateNaissance = "L'âge minimal de recensement est de 16 ans";
    } else if (ageEnAnnees > 25) {
      errors.dateNaissance = "L'âge maximal de recensement est de 25 ans";
    }
  }

  // Vérification que le parent est plus âgé que le recensé
  if (parent && dateNaissance.isValid() && dateNaissanceRecense) {
    const dateNaissanceEnfant = dayjs(
      `${dateNaissanceRecense.anneeNaissance}-${dateNaissanceRecense.moisNaissance}-${dateNaissanceRecense.jourNaissance}`,
      'YYYY-M-D',
    );
    if (dateNaissanceEnfant.isValid() && dateNaissance.isSameOrAfter(dateNaissanceEnfant, 'day')) {
      errors.dateNaissance = 'Le parent doit être plus âgé que le recensé';
    }
  }

  if (_.isEmpty(data.sexe)) {
    errors.sexe = 'Le sexe est obligatoire';
  }

  if (_.isEmpty(data.nomFamille)) {
    errors.nomFamille = 'Le nom de famille est obligatoire';
  }

  if (_.isEmpty(data.prenom)) {
    errors.prenom = 'Le prénom est obligatoire';
  }

  // Si les heures sont renseignées mais pas les minutes
  if (
    !_.isEmpty(data?.heureNaissance)
    && (_.isEmpty(data?.minuteNaissance) || _.isNil(data?.minuteNaissance))
  ) {
    errors.minuteNaissance = 'Les minutes sont obligatoires';
  }

  // Si les minutes de naissance sont renseignées mais pas les heures
  if (
    !_.isEmpty(data?.minuteNaissance)
    && (_.isEmpty(data?.heureNaissance) || _.isNil(data?.heureNaissance))
  ) {
    errors.heureNaissance = "L'heure de naissance est obligatoire";
  }

  if (_.isEmpty(data?.adresseNaissance?.libelleCommune)) {
    errors.adresseNaissance = 'La commune de naissance est obligatoire';
  }

  const codeDepartement = data?.adresseNaissance?.codeDepartement;
  const libelleDepartement = data?.adresseNaissance?.libelleDepartement;

  if (_.isEmpty(codeDepartement)) {
    errors.adresseNaissance = 'Le code départemental de naissance est obligatoire';
  }

  if (_.isEmpty(libelleDepartement)) {
    errors.adresseNaissance = 'Le libellé du département de naissance est obligatoire';
  }

  // Si on est à l'étranger, on n'est pas censés pouvoir sélectionner France dans le pays
  if (
    (codeDepartement === '99' || libelleDepartement === 'Pays et territoire étranger')
    && data?.adresseNaissance?.libellePays === 'France'
  ) {
    errors.adresseNaissance = 'Le pays n\'est pas en corrélation avec le département';
  }

  if (
    (_.isNil(data.adresseNaissance?.identifiantPays)
      || data.adresseNaissance?.identifiantPays === 0)
    && _.isEmpty(data.adresseNaissance?.libellePays)
  ) {
    errors.adresseNaissance = 'Le pays de naissance est obligatoire';
  }

  if (parent && (_.isNil(data?.identifiantNationalite) || data?.identifiantNationalite === 0)) {
    errors.identifiantNationalite = 'La nationalité est obligatoire';
  }
  

  return _.pickBy(errors, (value) => value !== '');
};

export default validationInformationsNaissance;
